import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Button,
  Grid,
  Input,
  InputAdornment,
  Typography
} from '@mui/material'
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined'
import CallMadeRounded from '@mui/icons-material/CallMadeRounded'
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined'
import SendRounded from '@mui/icons-material/SendRounded'
import DataTable from '../../components/DataTable'
import Header from '../../components/Headers/Header'
import CustomLoader from '../../components/Loader/CustomLoader'
import TableHeader from '../../components/TableHeader'
import {
  SectionCard,
  StatTile,
  raisedButtonSx
} from '../../components/UI/Depth'
import { influencerSidebarIcons } from '../../components/Sidebar/sidebarIcons'
import {
  getInfluencerDashboard,
  getInfluencerWithdrawRequests,
  createInfluencerWithdrawRequest
} from '../../apollo'
import useGlobalStyles from '../../utils/globalStyles'
import {
  getViewingInfluencerId,
  isAdminViewingInfluencer
} from '../../utils/influencerContext'
import { customStyles } from '../../utils/tableCustomStyles'
import { FieldLabel, FormFeedback, StatusPill, money } from './shared'

const GET_DASHBOARD = gql`
  ${getInfluencerDashboard}
`
const GET_REQUESTS = gql`
  ${getInfluencerWithdrawRequests}
`
const CREATE_REQUEST = gql`
  ${createInfluencerWithdrawRequest}
`

const statusTone = {
  REQUESTED: 'amber',
  TRANSFERRED: 'green',
  CANCELLED: 'rose'
}

function InfluencerWallet(props) {
  const { t } = props
  const globalClasses = useGlobalStyles()
  const [amount, setAmount] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const influencerId = getViewingInfluencerId()
  // An admin can raise the request for the influencer they are viewing
  const adminViewing = isAdminViewingInfluencer()
  const { data: dashboardData, refetch: refetchDashboard } = useQuery(
    GET_DASHBOARD,
    {
      variables: { influencerId },
      fetchPolicy: 'network-only'
    }
  )
  const { data: requestData, loading, refetch: refetchRequests } = useQuery(
    GET_REQUESTS,
    {
      variables: { influencerId },
      fetchPolicy: 'network-only'
    }
  )

  const [mutate, { loading: saving }] = useMutation(CREATE_REQUEST, {
    onCompleted: () => {
      setAmount('')
      setError('')
      setSuccess(t('WithdrawRequestSubmitted'))
      refetchDashboard()
      refetchRequests()
      setTimeout(() => setSuccess(''), 4000)
    },
    onError: err => {
      setSuccess('')
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const dashboard = dashboardData?.influencerDashboard
  const balance = Number(dashboard?.currentWalletAmount || 0)

  const onSubmit = event => {
    event.preventDefault()
    const value = Number(amount)
    // The server enforces this too; checking here saves a round trip
    if (!value || value <= 0) {
      setError(t('EnterValidAmount'))
      return
    }
    if (value > balance) {
      setError(t('NotEnoughBalance'))
      return
    }
    mutate({ variables: { amount: value, influencerId } })
  }

  const columns = [
    {
      name: t('RequestID'),
      selector: 'requestId',
      cell: row => (
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
          {row.requestId}
        </Typography>
      )
    },
    {
      name: t('Amount'),
      cell: row => <>{money(row.requestAmount)}</>
    },
    {
      name: t('Status'),
      cell: row => (
        <StatusPill tone={statusTone[row.status]}>
          {row.status
            ? row.status.charAt(0) + row.status.slice(1).toLowerCase()
            : '-'}
        </StatusPill>
      )
    },
    {
      name: t('Date'),
      cell: row => (
        <>{row.requestTime ? new Date(row.requestTime).toDateString() : '-'}</>
      )
    }
  ]

  const stats = [
    {
      label: t('WalletBalance'),
      value: money(balance),
      icon: AccountBalanceWalletOutlined,
      tone: 'violet'
    },
    {
      label: t('Withdrawn'),
      value: money(dashboard?.withdrawnWalletAmount),
      icon: CallMadeRounded,
      tone: 'slate'
    },
    {
      label: t('TotalEarnings'),
      value: money(dashboard?.totalEarnings),
      icon: PaymentsOutlined,
      tone: 'green'
    }
  ]

  return (
    <>
      <Header
        title={t('Wallet')}
        subtitle={t('SubtitleInfluencerWallet')}
        icon={influencerSidebarIcons.Wallet}
      />
      <Grid container spacing={2.5} mb={3}>
        {stats.map(stat => (
          <Grid item xs={12} sm={4} key={stat.label}>
            <StatTile {...stat} />
          </Grid>
        ))}
      </Grid>

      <SectionCard
        icon={SendRounded}
        tone="orange"
        title={t('RequestWithdraw')}
        subtitle={
          adminViewing
            ? t('RaisingOnBehalf')
            : `${t('AvailableToWithdraw')}: ${money(balance)}`
        }
        bodySx={{ pt: 2.5 }}>
        <form onSubmit={onSubmit}>
          <FieldLabel>{t('Amount')}</FieldLabel>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
            <Input
              id="input-withdraw-amount"
              name="input-withdraw-amount"
              placeholder="0.00"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              startAdornment={
                <InputAdornment position="start">£</InputAdornment>
              }
              disableUnderline
              className={globalClasses.input}
              sx={{ width: { xs: '100%', sm: 240 } }}
            />
            <Button
              type="button"
              onClick={() => setAmount(balance ? balance.toFixed(2) : '')}
              disabled={!balance}
              sx={{ textTransform: 'none', fontWeight: 700, mt: '6px' }}>
              {t('WithdrawAll')}
            </Button>
            <Button
              type="submit"
              disabled={saving || !amount}
              sx={{
                ...raisedButtonSx,
                mt: '6px',
                flex: { xs: '1 1 100%', sm: '0 0 auto' }
              }}>
              {t('Request')}
            </Button>
          </Box>
        </form>
        <FormFeedback success={success} error={error} />
      </SectionCard>

      {loading ? (
        <CustomLoader />
      ) : (
        <DataTable
          title={<TableHeader title={t('WithdrawRequests')} />}
          columns={columns}
          data={requestData?.influencerWithdrawRequests || []}
          customStyles={customStyles}
          pagination
          noDataComponent={
            <Box p={3}>
              <Typography sx={{ color: 'text.disabled' }}>
                {t('NoWithdrawRequests')}
              </Typography>
            </Box>
          }
        />
      )}
    </>
  )
}

export default withTranslation()(InfluencerWallet)
